/**
 * ApprovalCard
 *
 * Renders the pending approval surface for an `external-irreversible`
 * capability tool call. Shows the humanized action name + its args and offers
 * Approve / Reject. Returns null when no approval is pending.
 *
 * - Approve dispatches `aiAssistant/approval/approve` which the approval policy
 *   reacts to by calling `ResumeAgentRun` on the backend with `{ approved: true,
 *   toolName, args }`. The token is minted server-side.
 * - Reject dispatches `aiAssistant/approval/reject` which the approval policy
 *   handles by simply clearing the pending slice (no backend call).
 *
 * data-testids:
 * - `ai-assistant-approval-card`         — container
 * - `ai-assistant-approval-action-name`  — humanized action name
 * - `ai-assistant-approval-approve`      — Approve button
 * - `ai-assistant-approval-reject`       — Reject button
 */

import { Button, Text, useComputedColorScheme } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { aiAssistantTestIds } from '@domains/aiAssistant/testids';
import { useAIAssistantDispatch, useAIAssistantSelector } from '../state/store';
import classes from './ApprovalCard.module.css';

export interface ApprovalCardProps {
  className?: string;
}

export function ApprovalCard({ className }: ApprovalCardProps ) {
  const pendingApproval = useAIAssistantSelector(( s ) => s.pendingApproval );
  const decision = useAIAssistantSelector(( s ) => s.pendingApprovalDecision );
  const dispatch = useAIAssistantDispatch();
  const { t } = useTranslation( 'interactions' );
  const colorScheme = useComputedColorScheme( 'light' );

  if ( !pendingApproval ) return null;

  const isResolving = decision !== null;

  const handleApprove = () => {
    dispatch(
      { type: 'aiAssistant/approval/approve', payload: {}},
      { source: 'user' }
    );
  };

  const handleReject = () => {
    dispatch(
      { type: 'aiAssistant/approval/reject', payload: {}},
      { source: 'user' }
    );
  };

  return (
    <div
      data-testid={aiAssistantTestIds.approvalCard}
      className={`${classes.approvalCard} ${className || ''}`}
    >
      <div className={classes.approvalHeader}>
        <Text className={classes.approvalTitle}>
          {t( 'ai_assistant_approval_title', 'Approval required:' )}
        </Text>
        <Text
          data-testid={aiAssistantTestIds.approvalActionName}
          className={classes.approvalActionName}
        >
          {pendingApproval.humanizedName}
        </Text>
      </div>

      <div className={classes.argsBlock}>
        <Text className={classes.argsLabel}>
          {t( 'ai_assistant_approval_args', 'Arguments' )}
        </Text>
        <pre className={classes.argsContent}>
          {JSON.stringify( pendingApproval.args, null, 2 )}
        </pre>
      </div>

      <div className={classes.approvalActions}>
        <Button
          data-testid={aiAssistantTestIds.approvalApprove}
          size="compact-xs"
          variant={colorScheme === 'dark' ? 'filled' : 'light'}
          color="primary"
          onClick={handleApprove}
          loading={decision === 'approved'}
          disabled={isResolving}
        >
          {t( 'ai_assistant_approval_approve', 'Approve' )}
        </Button>
        <Button
          data-testid={aiAssistantTestIds.approvalReject}
          size="compact-xs"
          variant="subtle"
          color="gray"
          onClick={handleReject}
          disabled={isResolving}
        >
          {t( 'ai_assistant_approval_reject', 'Reject' )}
        </Button>
      </div>
    </div>
  );
}

export default ApprovalCard;
